import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { MentionProvider } from '@atlaskit/mention/types';
import type { ConversationAssistantConfig } from '@atlassian/conversation-assistant/types';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';

type JiraIssueCommentEditor = NonNullable<ConversationAssistantConfig['jiraIssueCommentEditor']>;

const REFRESH_ISSUE_PAGE_EVENT = 'legacy_refreshIssuePage';

const requestIssueSoftRefresh = (issueKey: string): void => {
	try {
		// Copy jiraBridge.refreshIssuePageThrowable in jira/src/packages/platform/bridge/src/jira-bridge.tsx
		// to avoid the L4 -> L3 Thunderstone dependency.
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		document.dispatchEvent(
			new CustomEvent(REFRESH_ISSUE_PAGE_EVENT, {
				detail: [issueKey, { mergeIntoCurrent: false, reason: 'SOFT_REFRESH' }],
			}),
		);
	} catch {
		// Refresh failures should not affect successful comment creation.
	}
};

const onCommentSave = async (issueIdOrKey: string, comment: DocNode): Promise<void> => {
	await performPostRequest(`/rest/api/3/issue/${encodeURIComponent(issueIdOrKey)}/comment`, {
		body: JSON.stringify({ body: comment }),
	});

	requestIssueSoftRefresh(issueIdOrKey);
};

export const useJiraIssueCommentEditor = (): JiraIssueCommentEditor => {
	const mentionProvider: MentionProvider = useMemo(
		() => ({
			filter: () => Promise.resolve([]),
			isFiltering: () => false,
			recordMentionSelection: () => undefined,
			shouldHighlightMention: () => false,
			subscribe: () => undefined,
			unsubscribe: () => undefined,
		}),
		[],
	);

	return useMemo(
		() => ({
			createMentionProvider: () => Promise.resolve(mentionProvider),
			onCommentSave,
		}),
		[mentionProvider],
	);
};
